// components/ReportCaseForm.js

import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import axios from "axios";
import Select from "react-select";
import { X, User, AlertTriangle, Folder, Briefcase, FileText, Shield } from "lucide-react";

const ReportCaseForm = ({ reporterId, onClose }) => {
    const modalRef = useRef(null);
    const [formData, setFormData] = useState({
        staffId: "",
        offense: "",
        resolutionNotes: "",
        department: "",
        reporterId: reporterId || "",
        projectId: "" // State to hold the selected project ID
    });

    const [staffList, setStaffList] = useState([]);
    const [projectList, setProjectList] = useState([]); // State for project list
    const [loading, setLoading] = useState(false);

    const staffOptions = staffList.map((staff) => ({
        value: staff._id,
        label: `${staff.firstName} ${staff.lastName} — ${staff.jobTitle || "No Title"}`,
        department: staff.department || "",
    }));

    const projectOptions = projectList.map((project) => ({ // Map projects to options
        value: project._id,
        label: project.projectTitle,
    }));

    const token = localStorage.getItem("token");

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [staffRes, projectRes] = await Promise.all([
                    axios.get(
                        "https://ims.holdthechild.net/api/staff",
                        { headers: { Authorization: `Bearer ${token}` } }
                    ),
                    axios.get(
                        "https://ims.holdthechild.net/api/projects",
                        { headers: { Authorization: `Bearer ${token}` } }
                    )
                ]);

                setStaffList(staffRes.data.data || []);
                setProjectList(projectRes.data.data || []);
            } catch (err) {
                toast.error("Failed to load data");
            }
        };

        fetchData();
    }, [token]);

    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!formData.staffId) {
            toast.error("Please select a staff member to report.");
            setLoading(false);
            return;
        }

        try {
            await axios.post(
                "https://ims.holdthechild.net/api/disciplinary-cases/report",
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setFormData({
                staffId: "",
                offense: "",
                resolutionNotes: "",
                department: "",
                reporterId: reporterId || "",
                projectId: ""
            });

            toast.success("Case reported successfully!");
            onClose?.();
        } catch (err) {
            toast.error("Failed to report case");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
            <div
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out"
            >
                {/* Header with Orange Theme */}
                <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500">
                    <div className="flex justify-between items-center">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-black">
                                    Report Case
                                </h2>
                                <div className="flex items-center mt-1 text-black text-sm">
                                    <Folder className="w-3 h-3 mr-1" />
                                    <span>Programs Department</span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Form Body */}
                <div className="p-4 sm:p-6">
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Staff to Report <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <Select
                                    options={staffOptions}
                                    onChange={(selected) =>
                                        setFormData({
                                            ...formData,
                                            staffId: selected?.value || "",
                                            department: selected?.department || "",
                                        })
                                    }
                                    placeholder="Select Staff..."
                                    isClearable
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            borderRadius: '0.75rem',
                                            minHeight: '44px',
                                            borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                                            boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                            '&:hover': {
                                                borderColor: '#F97316'
                                            }
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                            color: state.isSelected ? 'white' : '#1F2937',
                                            '&:hover': {
                                                backgroundColor: '#FFEDD5'
                                            }
                                        }),
                                    }}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Department
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Briefcase className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    readOnly
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Offense <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                    <AlertTriangle className="h-5 w-5 text-gray-400" />
                                </div>
                                <textarea
                                    name="offense"
                                    value={formData.offense}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                                    placeholder="Describe the offense..."
                                    required
                                    rows={3}
                                />
                            </div>
                        </div>

                        {/* Project Selection Field */}
                        {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Project (Optional)
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Folder className="h-5 w-5 text-gray-400" />
                                </div>
                                <Select
                                    options={projectOptions}
                                    onChange={(selected) =>
                                        setFormData({
                                            ...formData,
                                            projectId: selected?.value || null
                                        })
                                    }
                                    placeholder="Select a project..."
                                    isClearable
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            borderRadius: '0.75rem',
                                            minHeight: '44px',
                                            borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                                            boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                                            '&:hover': {
                                                borderColor: '#F97316'
                                            }
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                                            color: state.isSelected ? 'white' : '#1F2937',
                                            '&:hover': {
                                                backgroundColor: '#FFEDD5'
                                            }
                                        }),
                                    }}
                                />
                            </div>
                        </div> */}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Brief Explanation
                            </label>
                            <div className="relative">
                                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                    <FileText className="h-5 w-5 text-gray-400" />
                                </div>
                                <textarea
                                    name="resolutionNotes"
                                    value={formData.resolutionNotes}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                                    placeholder="Briefly explain the situation..."
                                    rows={3}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        Submit Report
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReportCaseForm;